import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Route, RouteStatus, RouteType } from 'fuel-benchmark-contracts';
import { CopyPlus, Pencil, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { Dialog } from '../../components/ui/Dialog';
import { InfoCell } from '../../components/ui/InfoCell';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatKm } from '../../lib/format';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { ROUTE_TYPES, routeStatusTone } from './condition-display';
import { RouteSheet } from './RouteSheet';

const PAGE_SIZE = 200;

export const RoutesTab = () => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [routeType, setRouteType] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState<Route | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [pendingVersion, setPendingVersion] = useState<Route | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const routes = useQuery(
    orpc.routes.list.queryOptions({
      input: {
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'code',
        sortDirection: 'asc',
        search: search.trim() === '' ? undefined : search.trim(),
        routeType: routeType === '' ? undefined : (routeType as RouteType),
        status: status === '' ? undefined : (status as RouteStatus),
      },
    }),
  );

  const newVersion = useMutation(orpc.routes.newVersion.mutationOptions());

  const onNewVersion = async (): Promise<void> => {
    if (!pendingVersion) {
      return;
    }

    setActionError(null);

    try {
      await newVersion.mutateAsync({ id: pendingVersion.id });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.routes.list.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
      ]);
      setPendingVersion(null);
    } catch (error) {
      setActionError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  const openEdit = useCallback((route: Route) => {
    setEditing(route);
    setIsSheetOpen(true);
  }, []);

  const columns: DataTableColumns<Route> = useMemo(
    () => [
      {
        accessorKey: 'code',
        header: t('conditions.routes.columns.code'),
        cell: ({ row }) => <span className="font-mono text-xs text-foreground">{row.original.code}</span>,
      },
      {
        accessorKey: 'name',
        header: t('conditions.routes.columns.name'),
        // Bounded so the description actually truncates: in an auto-layout table an unbounded cell grows to
        // its widest line, and a paragraph-long description would push the profile and the status out of view.
        cell: ({ row }) => (
          <InfoCell
            className="max-w-md"
            title={row.original.name}
            subline={row.original.description}
          />
        ),
      },
      {
        accessorKey: 'routeType',
        header: t('conditions.routes.columns.routeType'),
        cell: ({ row }) => t(`enums.routeType.${row.original.routeType}`),
      },
      {
        accessorKey: 'version',
        header: t('conditions.routes.columns.version'),
        cell: ({ row }) => <span className="text-muted-foreground">v{row.original.version}</span>,
      },
      {
        accessorKey: 'distanceKm',
        header: t('conditions.routes.columns.distance'),
        cell: ({ row }) => formatKm(row.original.distanceKm),
      },
      {
        id: 'elevation',
        // Sorted on the climb, which is the half the profile line leads with.
        accessorFn: (route) => route.elevationGainM ?? 0,
        header: t('conditions.routes.columns.elevation'),
        // Deliberately allowed to wrap: a fully surveyed profile is long, and forcing it onto one line
        // would push the status and the row actions off the side of the table.
        cell: ({ row }) => (
          <span className="block max-w-48 text-xs text-muted-foreground">{row.original.elevationProfile ?? t('common.notSet')}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: t('conditions.routes.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.status.${row.original.status}`)}
            tone={routeStatusTone(row.original.status)}
          />
        ),
      },
      {
        id: 'rowActions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('conditions.routes.edit')}
              onClick={() => openEdit(row.original)}
            >
              <Pencil className="size-3.5" />
            </Button>
            {row.original.status === 'ACTIVE' ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('conditions.routes.newVersion')}
                onClick={() => setPendingVersion(row.original)}
              >
                <CopyPlus className="size-3.5" />
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [openEdit, t],
  );

  const statusOptions: SelectOption[] = [
    { value: 'ACTIVE', label: t('enums.status.ACTIVE') },
    { value: 'SUPERSEDED', label: t('enums.status.SUPERSEDED') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('conditions.routes.searchPlaceholder')}
            aria-label={t('conditions.routes.searchPlaceholder')}
            className="max-w-xs"
          />
          <Select
            value={routeType}
            onChange={(event) => setRouteType(event.target.value)}
            options={ROUTE_TYPES.map((value) => ({ value, label: t(`enums.routeType.${value}`) }))}
            placeholder={t('conditions.routes.allRouteTypes')}
            aria-label={t('conditions.routes.allRouteTypes')}
            className="w-auto min-w-40"
          />
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            options={statusOptions}
            placeholder={t('conditions.routes.allStatuses')}
            aria-label={t('conditions.routes.allStatuses')}
            className="w-auto min-w-40"
          />
        </div>

        <Button
          onClick={() => {
            setEditing(null);
            setIsSheetOpen(true);
          }}
        >
          <Plus className="size-4" />
          {t('conditions.routes.add')}
        </Button>
      </div>

      {actionError !== null || routes.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError ?? t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={routes.data?.items ?? []}
        isLoading={routes.isPending}
        emptyMessage={t('conditions.routes.empty')}
      />

      <RouteSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        route={editing ?? undefined}
      />

      <Dialog
        isOpen={pendingVersion !== null}
        onClose={() => setPendingVersion(null)}
        title={t('conditions.routes.newVersionDialog.title')}
        description={
          pendingVersion
            ? t('conditions.routes.newVersionDialog.body', {
                code: pendingVersion.code,
                version: pendingVersion.version,
                nextVersion: pendingVersion.version + 1,
              })
            : undefined
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setPendingVersion(null)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={newVersion.isPending}
              onClick={() => void onNewVersion()}
            >
              {t('conditions.routes.newVersionDialog.confirm', { nextVersion: (pendingVersion?.version ?? 0) + 1 })}
            </Button>
          </>
        }
      />
    </div>
  );
};
